import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  MenuRounded,
  ExitToApp,
  Person,
  KeyboardArrowDownRounded,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useAppTheme } from '../../theme';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import ThemeSwitcher from './ThemeSwitcher';
import Wordmark from './Wordmark';
import HeaderActions from './HeaderActions';
import CommandBar from '../shell/CommandBar';
import OrgSwitcher from '../shell/OrgSwitcher';
import CommandPalette, { useCommandPalette } from '../../command/CommandPalette';
import { motion } from '../shell/shellTokens';

interface HeaderProps {
  /** Sidebar is showing at all — drives the hamburger's label and the wordmark. */
  open: boolean;
  handleDrawerToggle: () => void;
  /**
   * Current width of the nav column (0 / 76 / 280). The bar starts where the
   * rail ends, and animates on the rail's own curve so the two edges stay
   * welded together while it expands.
   */
  navWidth: number;
}

/**
 * The top bar.
 *
 * A light, quiet surface: the rail carries the colour, so anything strong up
 * here fights it. Layout is three tracks — controls / command / identity.
 *
 * It owns the command palette: `useCommandPalette` registers the global Ctrl+K
 * listener, so exactly one component may mount it.
 */
const Header: React.FC<HeaderProps> = ({ open, handleDrawerToggle, navWidth }) => {
  const { user, logout } = useAuth();
  const { colors } = useAppTheme();
  const navigate = useNavigate();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  // The existing Ctrl/Cmd+K palette (src/command/CommandPalette.tsx) — reused,
  // not reimplemented. CommandBar and the md-down search icon both open this.
  const { open: paletteOpen, setOpen: setPaletteOpen } = useCommandPalette();

  const handleProfileMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
  };

  const isMenuOpen = Boolean(anchorEl);
  const initial = user?.name?.charAt(0).toUpperCase() || 'U';
  const roleLabel = (user?.role || '').replace(/_/g, ' ');

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${navWidth}px)` },
          ml: { sm: `${navWidth}px` },
          backgroundColor: colors.backgroundPaper,
          backgroundImage: 'none',
          color: colors.textPrimary,
          borderBottom: `1px solid ${colors.border}`,
          // Same duration and easing as the rail's width transition, so the
          // AppBar's left edge tracks the rail exactly. MUI's `leavingScreen`
          // default (195ms, sharp) is close enough to look like a bug: the two
          // edges visibly separate and snap back together.
          transition: motion(reducedMotion, 'margin .22s cubic-bezier(.4,0,.2,1), width .22s cubic-bezier(.4,0,.2,1)'),
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, gap: 1 }}>
          {/* ---- left: drawer control + (only when the rail is hidden) brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            <Tooltip title={open ? 'Hide navigation' : 'Show navigation'}>
              <IconButton
                aria-label={open ? 'hide navigation' : 'show navigation'}
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  color: colors.textSecondary,
                  '&:hover': { backgroundColor: colors.background, color: colors.textPrimary },
                }}
              >
                <MenuRounded />
              </IconButton>
            </Tooltip>

            {/* The rail carries the wordmark; repeating it here shows the brand
                twice side by side. Render it only when the rail is hidden. */}
            {!open && <Wordmark size={26} tone="light" />}
          </Box>

          {/* ---- centre: the single global command surface */}
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
            <CommandBar onOpen={() => setPaletteOpen(true)} />
          </Box>

          {/* ---- right: org context, theme, actions, identity */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <OrgSwitcher />
            <ThemeSwitcher />
            <HeaderActions onOpenPalette={() => setPaletteOpen(true)} />

            <Box
              role="button"
              tabIndex={0}
              aria-label="account menu"
              aria-controls="primary-search-account-menu"
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setAnchorEl(e.currentTarget as HTMLElement);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                ml: 0.5,
                pl: 0.5,
                pr: { xs: 0.5, md: 1 },
                py: 0.5,
                borderRadius: 2,
                cursor: 'pointer',
                transition: motion(reducedMotion, 'background-color .15s ease'),
                '&:hover': { backgroundColor: colors.background },
                '&:focus-visible': { outline: `2px solid ${colors.primary}`, outlineOffset: 2 },
              }}
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#fff',
                  background: 'linear-gradient(135deg, #6366F1 0%, #2563EB 100%)',
                }}
              >
                {initial}
              </Avatar>
              <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0, lineHeight: 1.1 }}>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: colors.textPrimary,
                    whiteSpace: 'nowrap',
                    maxWidth: 150,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user?.name || 'User'}
                </Typography>
                {/* Role, not a decorative subtitle: in an ERP the caller's role
                    is what explains why a screen is read-only. */}
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    color: colors.textSecondary,
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {roleLabel.toLowerCase() || 'Member'}
                </Typography>
              </Box>
              <KeyboardArrowDownRounded
                sx={{ fontSize: 18, color: colors.textSecondary, display: { xs: 'none', md: 'block' } }}
              />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        id="primary-search-account-menu"
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isMenuOpen}
        onClose={handleProfileMenuClose}
        PaperProps={{
          elevation: 8,
          sx: { mt: 1, minWidth: 240, borderRadius: 2 },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                color: '#fff',
                background: 'linear-gradient(135deg, #6366F1 0%, #2563EB 100%)',
              }}
            >
              {initial}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {user?.name || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.email || 'user@example.com'}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider />
        <MenuItem
          component={RouterLink}
          to="/settings"
          onClick={(e: React.MouseEvent) => {
            // Real <a href>: let Ctrl/Cmd/Shift/middle-click open a new tab.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
            handleProfileMenuClose();
            navigate('/settings');
          }}
          sx={{ py: 1.5, gap: 1.5 }}
        >
          <Person fontSize="small" color="action" />
          <Typography variant="body2">My Profile</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ py: 1.5, gap: 1.5, color: 'error.main' }}>
          <ExitToApp fontSize="small" />
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>

      {/* One palette instance for the whole shell. */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
};

export default Header;
